<template>

    <!-- Providers -->
    <LoadingDiv v-if="!isReady" />

    <div class="sba-open-workbench" v-else>

      <div class="sba-open-head">
        <div class="sba-open-title">
          <label class="active teal-text">Provider Approvals</label>
          <h5>Pending Provider Sync</h5>
          <p>Select a provider from the approval list, inspect the details, and complete the sync action.</p>
        </div>

        <span class="sba-open-count">
          {{ providers.length }}
          <small>{{ providers.length === 1 ? "record" : "records" }}</small>
        </span>
      </div>

      <div class="row">

        <div class="col s12 m3">
          <div class="sba-approval-list sba-open-row">
          <h6>Approval List</h6>
          <ol>
            <li v-for="provider in providers" :key="provider.id">
              <a href="#" @click.prevent="handleSync(provider)">{{ provider.name }} </a> <span v-if="provider.pivot.status === 'a'"><small>(done)</small></span> 
            </li>
          </ol>

          <div class="section center" v-if="!providers.length">
            <h6>No Provider <em>found</em>...</h6>
          </div>

          <PaginationDiv :payLoad="allPages" @newPage="handlePage" v-if="providers.length > 10" />
          </div>

        </div>

        <div class="col s12 m9">

          <SyncProvider :oneProvider="provider" @clear="handleClear" @update="handleUpdate" v-if="provider" />
          
          <div class="section" v-else>
            <div class="card-panel sba-open-row">
              <span v-if="!isLoading">
              <h5>
                Select Provider
              </h5>
              <p>Click on the Provider to sync with 
                the easHMO application database!</p>
              </span>
              <span v-else>
                <h5>
                Selecting Provider for Syncing
              </h5>
              <p>Please wait while provider is loading...</p>
              </span>
            </div>
          </div>

          <!-- isLoading -->
              <div class="progress" v-if="isLoading">
                <div class="indeterminate"></div>
              </div>

        </div>

      </div>

    </div>

</template>

<script>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import getItem from "@/composables/getItem";
import getItems from "@/composables/getItems";
import SyncProvider from "@/components/provider/SyncProviderComponent.vue";
import LoadingDiv from "@/components/LoadingDiv.vue";
import PaginationDiv from "@/components/pager/PaginationDiv.vue";

export default {
  name: "ClientApprovalComponent",
  components: { SyncProvider, LoadingDiv, PaginationDiv },
  setup() {
    const response = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const currUrl = ref(null);
    const currHook = ref(null);
    const error = ref(null);
    const provider = ref(null);
    const searchItem = ref(null);
    const home = ref(null);
    const currTab = ref(null);
    const tabs = ref([]);

    // vue router
    const router = useRouter();

    // menu
    onMounted(() => {
      // load tab
      tabs.value.push({ id: 1, name: "Providers", title: "Home", icon: "arrow_back", url: "provider", hash: null, });
      tabs.value.push({ id: 2, name: "Providers", title: "View", icon: "visibility", url: "provider", hash: "#view", });
      tabs.value.push({ id: 3, name: "Providers", title: "Add Provider", icon: "add_circle", url: "providerAdd", hash: null, });
      tabs.value.push({ id: 4, name: "Providers", title: "Sync", icon: null, url: null, hash: null, });

      // set default
      currTab.value = tabs.value.find(x => x.title.includes("Sync"));
      // set home
      home.value = tabs.value.find(x => x.title.includes("Home"));
    });

    // handle curr status
    const handleActive = (tab) => {
      // refer
      if (tab.hash) {
        router.push({ name: tab.url, hash: tab.hash });
      } else {
        router.push({ name: tab.url });
      }
    };

    // numbering
    const numberBadge = (num) => {
      return (
        (allPages.value.current_page - 1) * allPages.value.per_page + num + 1
      );
    };

    // get function
    const getItemsFromApi = (table) => {
      (async () => {
        const { res, err } = await getItems(table);

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready, loading is complete
        isReady.value = true;
        isLoading.value = false;
      })();
    };

    // starter
    onMounted(() => {
      currUrl.value = "integration/oneprovider/provider-sync/pending";
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value);
    });

    // clear
    const handleClear = (val) => {
      if (val) {
       // clear provider
       provider.value = null;
      }
    }

    // clear
    const handleUpdate = (val) => {
      if (val) {
       // update provider
       response.value.data.forEach((x) => {
        if (x.id === val.id) {
          // get all
          x.pivot.status = val;
        }
       });
      }
    }

    // sync
    const handleSync = (val) => {
      // start
      provider.value = null;
      // start loading
      isLoading.value = true;

      // confirm availabity again
      (async () => {
        const { res, err } = await getItem("integration/oneprovider/provider-sync/providers", val.opid);

        if (res.value) {
          provider.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // end
        isLoading.value = false;

      })();
    }

    // switch page
    const handlePage = (page) => {
      // start laoding
      isLoading.value = true;
      scrollToPageTop();
      // get new page items from function
      getItemsFromApi(`${currUrl.value}${currHook.value}page=${page}`); //
    };

    const scrollToPageTop = () => {
      requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    };

    // output results
    const providers = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value.data;
      }
    });

    // output pages
    const allPages = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value; // get all
      }
    });

    return {
      handleClear,
      handleUpdate,
      handleActive,
      home,
      currTab,
      tabs,
      numberBadge,
      handlePage,
      handleSync,
      isReady,
      isLoading,
      error,
      provider,
      searchItem,
      providers,
      allPages,
    };
  },
};
</script>

<style scoped>
.progress {
  margin-top: -30px;
}

.sba-open-workbench {
  color: #263238;
}

.sba-open-head,
.sba-open-row,
.sba-approval-list {
  background: #ffffff;
  border: 1px solid #dfe7ea;
  border-radius: 6px;
  box-shadow: none;
}

.sba-open-head {
  align-items: flex-end;
  border-color: #c4d3d9;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 24px 26px;
}

.sba-open-title h5 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.sba-open-title p {
  color: #607d8b;
  line-height: 1.45;
  margin: 6px 0 0;
}

.sba-open-count {
  background: #e0f2f1;
  border: 1px solid #b2dfdb;
  border-radius: 18px;
  color: #00695c;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  font-weight: 700;
  line-height: 1.25;
  padding: 7px 12px;
  text-align: right;
}

.sba-open-count small {
  color: #455a64;
  font-size: 0.76rem;
}

.sba-approval-list {
  overflow: hidden;
}

.sba-approval-list ul {
  margin: 0;
}

.sba-approval-list li + li {
  border-top: 1px solid #edf2f4;
}

.sba-approval-list a {
  color: #263238;
  display: block;
  font-weight: 700;
  padding: 13px 14px;
}

.sba-approval-list small {
  color: #00897b;
  display: inline-block;
  font-weight: 700;
  padding: 0 14px 12px;
}

.sba-open-row {
  padding: 22px;
}

.sba-open-row h5 {
  color: #263238;
  font-size: 1.25rem;
  font-weight: 700;
  margin-top: 0;
}

.sba-open-row p {
  color: #607d8b;
  line-height: 1.45;
  margin-bottom: 0;
}

@media only screen and (max-width: 900px) {
  .sba-open-head {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media only screen and (max-width: 600px) {
  .sba-open-head {
    gap: 14px;
    padding: 20px;
  }

  .sba-open-title h5 {
    font-size: 1.35rem;
    line-height: 1.25;
  }

  .sba-open-count,
  .sba-open-row,
  .sba-approval-list {
    width: 100%;
  }

  .sba-open-workbench > .row {
    margin-left: 0;
    margin-right: 0;
  }

  .sba-open-workbench > .row > .col {
    padding-left: 0;
    padding-right: 0;
  }

  .sba-approval-list {
    margin-bottom: 16px;
  }

  .sba-approval-list a {
    min-height: 44px;
  }

  .sba-open-row {
    padding: 18px;
  }
}
</style>
